export interface HelpTextParagraphBlock {
  type: 'paragraph'
  text: string
}

export interface HelpTextListBlock {
  type: 'list'
  items: { label: string; value: string }[]
}

export interface HelpTextTableBlock {
  type: 'table'
  rows: { label: string; values: string[] }[]
}

export type HelpTextBlock = HelpTextParagraphBlock | HelpTextListBlock | HelpTextTableBlock

// A line like "A: 2010 / 1980" or "Medium Duty: Box mounted to class 6+ chassis." — short
// label, colon, then a non-empty value. Bounding the label length and excluding periods
// avoids misfiring on a sentence that happens to contain a colon (e.g. "...vehicle. Examples:
// A 6x4 vehicle will have...", where "Examples" is a sentence continuation, not a chart label —
// real chart labels never contain a period).
const KEY_VALUE_LINE = /^([^\n:.]{1,60}):\s+(\S.*)$/

// A measurement-like cell, e.g. 36", 48"", 70.5" — one or two trailing inch-mark characters
// (authors commonly double the quote when pasting from Excel).
const TABLE_CELL = /^\d+(?:\.\d+)?"{1,2}$/

// Splits on sentence-ending punctuation followed by whitespace and a capital letter/digit/open
// paren — a deliberately conservative boundary so common abbreviations ("e.g.", "Dr.", "3.5")
// rarely misfire, since their next character is lowercase or another digit, not a new sentence.
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=[A-Z0-9(])/

/**
 * Breaks a single authored line into one line per sentence, so a help text cell with no
 * manual line breaks (the common case — most authors don't use Alt+Enter) still renders
 * as readable separate lines instead of one dense run-on paragraph.
 */
function splitIntoSentenceLines(line: string): string[] {
  return line.split(SENTENCE_BOUNDARY).map((s) => s.trim()).filter(Boolean)
}

/**
 * Detects a real multi-column table row, e.g. `Sleeper Length as Measured (in) 36" 48" 63" 70"`
 * — a row label followed by 2+ space-separated measurement cells. Requiring at least 2 trailing
 * cells (not just 1) is what distinguishes this from a plain "Label: value" line.
 */
function matchTableRow(line: string): { label: string; values: string[] } | null {
  const tokens = line.split(/\s+/)
  let cellCount = 0
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (TABLE_CELL.test(tokens[i])) cellCount++
    else break
  }
  if (cellCount < 2) return null

  const label = tokens.slice(0, tokens.length - cellCount).join(' ').replace(/:\s*$/, '').trim()
  if (!label) return null

  const values = tokens.slice(tokens.length - cellCount).map((t) => t.replace(/"{1,2}$/, '"'))
  return { label, values }
}

/**
 * Splits DB-authored help text into paragraph, label/value-list, and table blocks:
 * - a run of consecutive "Label: value" lines (e.g. a VIN year decode chart) becomes a list block
 * - a run of consecutive multi-column rows with a matching cell count (e.g. a sleeper size chart)
 *   becomes a table block
 * - everything else stays as paragraph text
 *
 * Paragraph line breaks: if the author already used manual line breaks anywhere in this text
 * (Alt+Enter in Excel), each of those lines is trusted as a deliberate point and rendered as-is
 * — never re-split by sentence, so a point spanning two sentences on one authored line stays
 * one point. Sentence auto-splitting only kicks in as a fallback when the whole cell has no
 * manual line breaks at all (the common case for content nobody bothered to format).
 */
export function parseHelpTextBlocks(text: string): HelpTextBlock[] {
  const hasManualLineBreaks = /\r?\n/.test(text)
  const lines = text.split(/\r?\n/)
  const blocks: HelpTextBlock[] = []
  let paragraphLines: string[] = []
  let listItems: { label: string; value: string }[] = []
  let tableRows: { label: string; values: string[] }[] = []
  let tableColumnCount: number | null = null

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      const sentenceLines = hasManualLineBreaks ? paragraphLines : paragraphLines.flatMap(splitIntoSentenceLines)
      blocks.push({ type: 'paragraph', text: sentenceLines.join('\n') })
      paragraphLines = []
    }
  }
  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push({ type: 'list', items: listItems })
      listItems = []
    }
  }
  const flushTable = () => {
    if (tableRows.length > 0) {
      blocks.push({ type: 'table', rows: tableRows })
      tableRows = []
      tableColumnCount = null
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) {
      flushParagraph()
      flushList()
      flushTable()
      continue
    }

    const tableRow = matchTableRow(line)
    if (tableRow) {
      if (tableColumnCount !== null && tableRow.values.length !== tableColumnCount) {
        flushTable()
      }
      flushParagraph()
      flushList()
      tableColumnCount = tableRow.values.length
      tableRows.push(tableRow)
      continue
    }
    flushTable()

    const match = line.match(KEY_VALUE_LINE)
    if (match) {
      flushParagraph()
      listItems.push({ label: match[1].trim(), value: match[2].trim() })
    } else {
      flushList()
      paragraphLines.push(line)
    }
  }
  flushParagraph()
  flushList()
  flushTable()

  return blocks
}
